import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { FiChevronDown } from "react-icons/fi";
import clsx from "clsx";

interface Option {
  value: string;
  label: string;
}

interface SelectorProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  "data-testid"?: string;
}

export const Selector: React.FC<SelectorProps> = ({
  options,
  value,
  onChange,
  className,
  label,
  required,
  placeholder = "",
  "data-testid": dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({ top: 0, left: 0, width: 0, maxHeight: 0 });

  useEffect(() => {
    // composedPath rather than event.target: inside the booking widget's
    // shadow root a document-level listener sees the retargeted shadow host,
    // which is never "inside" the dropdown, so every click closed the menu.
    const handleClickOutside = (event: MouseEvent) => {
      const path = event.composedPath();
      const within = (el: HTMLElement | null) =>
        !!el &&
        path.some(
          (node) => node === el || (node instanceof Node && el.contains(node)),
        );
      if (!within(dropdownRef.current) && !within(menuRef.current)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const updatePosition = () => {
      if (!buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const gap = 4;
      const margin = 8;
      const preferred = 280;
      const spaceBelow = window.innerHeight - rect.bottom - gap - margin;
      const spaceAbove = rect.top - gap - margin;
      const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
      const available = openUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(80, Math.min(preferred, available));
      setMenuStyle({
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
    };
    updatePosition();
    const closeOnScroll = (event: Event) => {
      if (menuRef.current && event.target instanceof Node && menuRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  // The menu escapes overflow clipping by portalling out of the tree. In the
  // booking widget that tree lives in a shadow root whose stylesheet cannot
  // reach document.body, so the portal must stay inside the shadow root.
  // Only read once open — the ref is null before mount.
  const rootNode = isOpen ? dropdownRef.current?.getRootNode() : undefined;
  const portalTarget =
    rootNode instanceof ShadowRoot ? rootNode : document.body;

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <div className="flex items-end">
          <label className="text-xs font-medium leading-[1.67] text-ink">
            {label}
          </label>
          {required && (
            <span className="ml-1 text-status-error" aria-hidden="true">
              *
            </span>
          )}
        </div>
      )}
      <div className="relative w-full" ref={dropdownRef}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            "flex w-full items-center justify-between",
            "px-[13px] py-[9px]",
            "!border !border-border !bg-surface-card",
            "!rounded-md",
            "text-sm font-medium leading-[1.71] text-ink",
            "transition-colors hover:border-border",
            "focus:border-primary focus:outline-none",
            className,
          )}
          data-testid={dataTestId}
        >
          <span>{selectedOption?.label || placeholder}</span>
          <FiChevronDown
            className={clsx(
              "h-5 w-5 text-ink transition-transform",
              isOpen && "rotate-180 transform",
            )}
          />
        </button>

        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              onMouseDown={(e) => e.stopPropagation()}
              className="selector-menu fixed z-[2147483001] overflow-y-auto rounded-xl border border-border bg-surface-card py-1 shadow-lg"
              style={{
                top: menuStyle.top,
                bottom: menuStyle.bottom,
                left: menuStyle.left,
                width: menuStyle.width,
                maxHeight: menuStyle.maxHeight,
              }}
            >
              {options.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    "w-full px-[13px] py-[9px]",
                    "text-left text-sm leading-[1.71]",
                    "transition-colors hover:bg-surface-page",
                    "text-ink",
                    value === option.value ? "font-medium" : "font-normal",
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>,
            portalTarget,
          )}
      </div>
    </div>
  );
};
